import { type Head } from "@unhead/vue";
import type { IResearchTopic } from "~/types";

enum TwitterCardType {
  Summary = "summary",
  SummaryLargeImg = "summary_large_image"
}

interface IFrontiersMetaInfo {
  title: string;
  description: string;
  keywords: string[];
  image?: string;
  url?: string;
  type?: string;
  frontiersType?: string;
  twitterSite?: string;
  canonicalUrl: string;
}

export const usePageMetaInfo = () => {
  function removeHtmlTags(stringWithHtml: string) {
    const regex = /(<([^>]+)>)/gi;
    return stringWithHtml.replace(regex, "");
  }

  const getKeywords = (researchTopic: IResearchTopic): string[] => {
    const specialtyJournals = researchTopic.journals
      .filter((j) => !!j.specialtyId)
      .map((j) => j.title.split("Frontiers in ").filter((f) => f)[0]);

    const sectionNames = [
      ...new Set(
        researchTopic.journals
          .map((j) => (j.sections || []).map((s) => s.title))
          .flat()
          .reverse()
      )
    ];

    return [...specialtyJournals, ...sectionNames, "Research Topics"];
  };

  const { baseUrl, spaceName, isCallForPapersVisible } =
    useRuntimeConfig().public;
  const { getTerm } = useTerminologyStore();

  const getMetaInfo = (metaInfo: IFrontiersMetaInfo): Head => {
    if (!metaInfo.description) {
      metaInfo.description = "";
    } else {
      metaInfo.description = removeHtmlTags(metaInfo.description);
    }

    const description =
      metaInfo.description.length > 160
        ? metaInfo.description.substring(0, 157) + "..."
        : metaInfo.description;
    const meta = [
      {
        hid: "description",
        property: "description",
        name: "description",
        content: description
      },
      {
        hid: "og:title",
        property: "og:title",
        name: "title",
        content: metaInfo.title
      },
      {
        hid: "og:description",
        property: "og:description",
        name: "description",
        content: description
      },
      {
        hid: "keywords",
        name: "keywords",
        content: metaInfo.keywords ? metaInfo.keywords.join(",") : ""
      },
      {
        hid: "og:site_name",
        property: "og:site_name",
        name: "site_name",
        content: spaceName
      }
    ];

    if (metaInfo.image) {
      meta.push({
        hid: "og:image",
        property: "og:image",
        name: "image",
        content: metaInfo.image
      });
    }

    if (metaInfo.type) {
      meta.push({
        hid: "og:type",
        property: "og:type",
        name: "type",
        content: metaInfo.type
      });
    }

    if (metaInfo.url) {
      meta.push({
        hid: "og:url",
        property: "og:url",
        name: "url",
        content: metaInfo.url
      });
    }

    if (metaInfo.frontiersType) {
      meta.push({
        hid: "frontiers:type",
        property: "frontiers:type",
        name: "frontiers-type",
        content: metaInfo.frontiersType
      });
    }

    if (metaInfo.twitterSite) {
      meta.push({
        hid: "twitter:site",
        name: "twitter:site",
        content: metaInfo.twitterSite
      });
    }

    meta.push({
      hid: "twitter:card",
      name: "twitter:card",
      content: TwitterCardType.SummaryLargeImg
    });

    return {
      title: metaInfo.title,
      link: [{ rel: "canonical", href: metaInfo.canonicalUrl }],
      meta
    };
  };

  const getResearchTopicDetailsPageMetaInfo = (
    researchTopic: IResearchTopic,
    page?: string
  ): Head => {
    const cleanedDescription = removeHtmlTags(
      removeHtmlTags(researchTopic.description)
    );
    const description =
      cleanedDescription.length > 200
        ? `${cleanedDescription.substring(0, 197)}...`
        : cleanedDescription;
    const absoluteUrl = `${baseUrl}/research-topics/${researchTopic.id}/${researchTopic.slug}${page}`;
    const frontiersType =
      isCallForPapersVisible && researchTopic.isOpenForSubmission
        ? getTerm("callForPapers")
        : getTerm("researchTopic");

    const pageMetaInfo = getMetaInfo({
      title: `${spaceName} | ${useSanitizeHtml(researchTopic.title, {
        ALLOWED_TAGS: []
      })}`,
      description,
      frontiersType,
      type: "website",
      image: researchTopic.imageUrl,
      keywords: getKeywords(researchTopic),
      url: absoluteUrl,
      canonicalUrl: absoluteUrl
    });

    return pageMetaInfo;
  };

  return {
    getResearchTopicDetailsPageMetaInfo
  };
};
